import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

// Superuser telegram IDs
const SUPERUSER_IDS = [7812065632]; // @TheAwakenedOleks

export default function PaymentWall() {
  const { user, setPage, refreshUser, testMode, setUser, currentCompany } = useApp();
  const { t } = useI18n();
  
  // Superuser can skip payment (only when NOT in test mode)
  const isSuperuser = user && SUPERUSER_IDS.includes(user.telegram_id) && !testMode;
  const canTrial = user && !user.has_paid_access && !user.trial_used;
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [transferDetails, setTransferDetails] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('bank_transfer');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState(null);
  const [file, setFile] = useState(null);
  const [trialLoading, setTrialLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    fontSize: 13,
    fontWeight: 600,
  };

  useEffect(() => {
    loadStatus();
  }, [currentCompany?.id]);

  async function loadStatus() {
    setLoading(true);
    try {
      const qs = currentCompany?.id ? `?company_id=${currentCompany.id}` : '';
      const { data } = await api.get(`/gateway-payments/my/status${qs}`);
      setStatus(data);
      
      if (data.has_gateway_contract && !data.activation_payment?.id) {
        // Create activation payment if not exists
        await api.post(`/gateway-payments/my/create-activation${qs}`);
        // Reload status
        const { data: newData } = await api.get(`/gateway-payments/my/status${qs}`);
        setStatus(newData);
      }
      
      // Load transfer details
      const { data: details } = await api.get('/gateway-payments/my/transfer-details');
      setTransferDetails(details);
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка загрузки' });
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmPayment() {
    if (!status?.activation_payment?.id) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('payment_id', status.activation_payment.id);
      if (file) {
        formData.append('file', file);
      }
      
      await api.post('/gateway-payments/my/confirm', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setMessage({ type: 'success', text: t('payment.pending_verification') });
      setShowConfirmModal(false);
      loadStatus();
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || 'Ошибка' });
    } finally {
      setUploading(false);
    }
  }

  async function enableTrial() {
    setTrialLoading(true);
    try {
      await api.post('/trial/enable');
      const me = await api.get('/me');
      setUser(me.data);
      setMessage({ type: 'success', text: '✅ Временный доступ активирован. Доступна 1 proforma.' });
      setPage('proforma');
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || e.response?.data?.detail || 'Ошибка' });
    } finally {
      setTrialLoading(false);
    }
  }

  async function startCardPayment() {
    setCardLoading(true);
    try {
      const { data } = await api.post('/payments/contract/checkout', {});
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setMessage({ type: 'error', text: 'Не удалось создать Stripe сессию' });
      }
    } catch (e) {
      setMessage({ type: 'error', text: e.userMessage || e.response?.data?.detail || 'Ошибка Stripe' });
    } finally {
      setCardLoading(false);
    }
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 60 }}>
        <p>⏳ {t('common.loading')}...</p>
      </div>
    );
  }

  // If gateway is active, redirect to dashboard
  if (status?.gateway_active) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 40 }}>
        <div style={{ fontSize: 72, marginBottom: 16 }}>✅</div>
        <h2>{t('payment.confirmed')}</h2>
        <p style={{ opacity: 0.7, marginBottom: 24 }}>Gateway активен. Вы можете выставлять фактуры.</p>
        <button className="btn btn-primary" onClick={() => setPage('invoices_out')}>
          📄 Создать фактуру
        </button>
      </div>
    );
  }

  // Pending verification
  if (status?.activation_payment?.status === 'pending_verification') {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 40 }}>
        <div style={{ fontSize: 72, marginBottom: 16 }}>⏳</div>
        <h2>{t('payment.pending_verification')}</h2>
        <p style={{ opacity: 0.7, marginBottom: 16 }}>
          Ваш платёж на рассмотрении. Обычно это занимает до 24 часов.
        </p>
        <p style={{ opacity: 0.5, fontSize: 13 }}>
          Подтверждено: {status.activation_payment.confirmed_at ? new Date(status.activation_payment.confirmed_at).toLocaleString() : '-'}
        </p>
        <button className="btn btn-secondary" onClick={() => setPage('dashboard')} style={{ marginTop: 24 }}>
          ← На главную
        </button>
      </div>
    );
  }

  // Rejected
  if (status?.activation_payment?.status === 'rejected') {
    return (
      <div className="page" style={{ paddingTop: 40 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 72, marginBottom: 16 }}>❌</div>
          <h2>{t('payment.rejected')}</h2>
        </div>
        {status.activation_payment.rejection_reason && (
          <div style={{
            background: 'rgba(220,38,38,0.2)',
            border: '1px solid rgba(220,38,38,0.5)',
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
          }}>
            <b>Причина:</b> {status.activation_payment.rejection_reason}
          </div>
        )}
        <p style={{ opacity: 0.7 }}>Пожалуйста, проверьте правильность перевода и попробуйте снова.</p>
        <button className="btn btn-primary" onClick={loadStatus} style={{ marginTop: 16 }}>
          🔄 Проверить статус
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <h1>💳 {t('payment.title')}</h1>
      
      {/* Superuser skip option */}
      {isSuperuser && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(124,58,237,0.2), rgba(139,92,246,0.1))',
          border: '1px solid rgba(124,58,237,0.4)',
          padding: 16,
          borderRadius: 12,
          marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 20 }}>👑</span>
            <b>Режим суперпользователя</b>
          </div>
          <p style={{ margin: '0 0 12px', opacity: 0.8, fontSize: 14 }}>
            Вы можете пропустить оплату и сразу перейти к функциям платформы.
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => setPage('invoices_out')}
            style={{ width: '100%' }}
          >
            ⚡ Пропустить и перейти к фактурам
          </button>
        </div>
      )}
      
      {message && (
        <div className={`banner ${message.type === 'error' ? 'banner-warning' : 'banner-success'}`} 
             style={{ marginBottom: 16 }}>
          {message.text}
        </div>
      )}

      {/* Summary */}
      <div className="detail-card" style={{ marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 12px' }}>💰 К оплате</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span>Активация (разово)</span>
          <b style={{ color: '#10b981' }}>1 200 PLN</b>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, opacity: 0.7 }}>
          <span>Далее ежемесячно</span>
          <span>350 PLN/мес</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', opacity: 0.7 }}>
          <span>Комиссия при выводе</span>
          <span>6%</span>
        </div>
      </div>

      {canTrial && (
        <div className="detail-card" style={{ marginBottom: 20, border: '1px dashed rgba(124,58,237,0.5)' }}>
          <h3 style={{ margin: '0 0 8px' }}>✨ Временный доступ</h3>
          <p style={{ opacity: 0.8, fontSize: 13, marginBottom: 12 }}>
            Можно создать <b>1 proforma</b>. Для конвертации в фактуру и вывода средств потребуется оплата 1200 PLN.
          </p>
          <button className="btn btn-secondary" onClick={enableTrial} disabled={trialLoading} style={{ width: '100%' }}>
            {trialLoading ? '⏳ Включаем...' : 'Включить временный доступ'}
          </button>
        </div>
      )}

      {/* Payment methods */}
      <h3 style={{ marginBottom: 12 }}>Выберите способ оплаты</h3>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {/* Bank Transfer - active */}
        <div 
          onClick={() => setSelectedMethod('bank_transfer')}
          style={{
            padding: 16,
            borderRadius: 12,
            border: selectedMethod === 'bank_transfer' ? '2px solid #7c3aed' : '1px solid rgba(255,255,255,0.1)',
            background: selectedMethod === 'bank_transfer' ? 'rgba(124,58,237,0.1)' : 'rgba(255,255,255,0.03)',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <b>🏦 {t('payment.bank_transfer')}</b>
              <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: 13 }}>Przelew bankowy</p>
            </div>
            <span style={{ color: '#10b981', fontSize: 13 }}>✓ Dostępny</span>
          </div>
        </div>

        {/* Card - Stripe */}
        <div 
          onClick={() => setSelectedMethod('card')}
          style={{
            padding: 16,
            borderRadius: 12,
            border: selectedMethod === 'card' ? '2px solid #7c3aed' : '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(255,255,255,0.03)',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <b>💳 Apple Pay / Карта / BLIK</b>
              <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: 13 }}>Visa/Mastercard, Apple Pay, BLIK (Stripe)</p>
            </div>
            <span style={{ color: '#10b981', fontSize: 12 }}>✓ Доступно</span>
          </div>
        </div>

        {/* BLIK - stub */}
        <div 
          onClick={() => setSelectedMethod('blik')}
          style={{
            padding: 16,
            borderRadius: 12,
            border: selectedMethod === 'blik' ? '2px solid #7c3aed' : '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(255,255,255,0.03)',
            cursor: 'pointer',
            opacity: 0.6,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <b>📱 BLIK</b>
              <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: 13 }}>Szybki przelew</p>
            </div>
            <span style={{ color: '#f59e0b', fontSize: 12 }}>🕐 {t('payment.coming_soon')}</span>
          </div>
        </div>
      </div>

      {/* Bank Transfer Details */}
      {selectedMethod === 'bank_transfer' && transferDetails && (
        <div className="detail-card" style={{ marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 12px' }}>📋 Dane do przelewu</h3>
          
          <div style={{ fontSize: 14, lineHeight: 1.8 }}>
            <div><b>Odbiorca:</b> {transferDetails.recipient}</div>
            <div><b>IBAN:</b> <span style={{ fontFamily: 'monospace' }}>{transferDetails.iban}</span></div>
            <div><b>Kwota:</b> {transferDetails.amount} {transferDetails.currency}</div>
            <div><b>Tytuł:</b> <span style={{ color: '#f59e0b' }}>{transferDetails.title}</span></div>
          </div>
          
          <div style={{ 
            marginTop: 12, 
            padding: '8px 12px', 
            background: 'rgba(245,158,11,0.1)', 
            borderRadius: 6,
            fontSize: 12,
            color: '#f59e0b'
          }}>
            ⚠️ Tytuł przelewu jest ważny! Pozwala nam zidentyfikować Twoją płatność.
          </div>
          
          <button 
            className="btn btn-secondary" 
            onClick={() => copyToClipboard(transferDetails.full_text)}
            style={{ marginTop: 16, width: '100%' }}
          >
            {copied ? '✅ Skopiowano!' : '📋 Kopiuj dane przelewu'}
          </button>
        </div>
      )}

      {/* Stub modals for Card/BLIK */}
      {selectedMethod === 'card' && (
        <div className="detail-card" style={{ marginBottom: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>💳</div>
          <h3>Оплата: Apple Pay / Карта / BLIK (Stripe)</h3>
          <p style={{ opacity: 0.7, marginBottom: 8 }}>
            Онбординг 1200 PLN + подписка 350 PLN/мес (первое списание через 30 дней).
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={badgeStyle}> Apple Pay</span>
            <span style={badgeStyle}>💳 Карта</span>
            <span style={badgeStyle}>🇵🇱 BLIK</span>
          </div>
          <button className="btn btn-primary" onClick={startCardPayment} disabled={cardLoading}>
            {cardLoading ? '⏳ Создаём оплату...' : 'Оплатить (Apple Pay / Карта / BLIK)'}
          </button>
        </div>
      )}

      {selectedMethod === 'blik' && (
        <div className="detail-card" style={{ marginBottom: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📱</div>
          <h3>{t('payment.blik_coming_soon')}</h3>
          <p style={{ opacity: 0.7 }}>
            BLIK będzie dostępny wkrótce.<br />
            Użyj teraz przelewu bankowego.
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="actions">
        {selectedMethod === 'bank_transfer' && (
          <button 
            className="btn btn-primary"
            onClick={() => setShowConfirmModal(true)}
          >
            ✅ {t('payment.i_paid')}
          </button>
        )}
        <button className="btn btn-secondary" onClick={() => setPage('dashboard')}>
          ← Назад
        </button>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 16,
        }}>
          <div style={{
            background: '#1a1a2e',
            borderRadius: 12,
            padding: 24,
            maxWidth: 400,
            width: '100%',
          }}>
            <h2 style={{ marginTop: 0 }}>💳 Подтверждение оплаты</h2>
            
            <p style={{ opacity: 0.7 }}>
              Подтвердите, что вы совершили перевод на сумму <b>1200 PLN</b>.
            </p>
            
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                Скриншот подтверждения (опционально):
              </span>
              <input 
                type="file" 
                accept="image/*,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                style={{
                  width: '100%',
                  padding: 8,
                  borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                }}
              />
            </label>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(false)}
                disabled={uploading}
              >
                Отмена
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmPayment}
                disabled={uploading}
              >
                {uploading ? '⏳...' : '✅ Подтвердить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
